import { faPause } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CallContextPanel } from "@notifications/CallContextPanel";
import { inboundCallerName } from "@notifications/inboundCallerName";
import {
  type IncomingCall,
  type LiveCallStatus,
  useIncomingCallStore,
} from "@notifications/incomingCallStore";
import { useHeldFor } from "@notifications/useHeldFor";
import { contextCallerName, useCallContext } from "@phone/callContextClient";
import { OutcomeChip } from "@phone/OutcomeChip";
import {
  formatSeconds,
  OUTBOUND_ENDED_UNREAD,
  outboundCallOutcome,
} from "@phone/phoneCallOutcome";
import { disclosureNoteFor } from "@phone/phoneCallsClient";
import { formatPhoneNumber } from "@phone/phoneSetupClient";
import {
  answerIncoming,
  declineIncoming,
  hangupSoftphoneCall,
  toggleSoftphoneMute,
  useSoftphoneStore,
} from "@phone/softphone";
import { useOutboundLines } from "@phone/useOutboundLines";
import {
  Button,
  cn,
  PaperPanel,
  StickyNote,
  Swipe,
} from "@scribenote/brian-design-system";
import { useOpenCalls } from "@shell/navigation";
import { useEffect, useState } from "react";

import { useDragOffset } from "./useDragOffset";

const PHASE: Partial<Record<LiveCallStatus, { word: string; aside: string }>> =
  {
    dialing: { word: "Connecting", aside: "one moment…" },
    out_dialing_staff: { word: "Calling", aside: "connecting your headset…" },
    out_awaiting_confirmation: { word: "Calling", aside: "connecting…" },
    out_dialing_recipient: { word: "Calling", aside: "ringing…" },
    out_disclosing: {
      word: "Recording notice",
      aside: "they're hearing the notice…",
    },
    out_connecting: { word: "Connecting", aside: "one moment…" },
  };

export const PULSE_DOT =
  "size-1.5 rounded-full bg-ink-working [animation:bds-pulse_1.6s_ease-in-out_infinite] motion-reduce:animate-none";

/** Counts from the bridge; inbound `started_at` predates it, so inbound uses `bridgedAtMs`. */
export const useTalkTime = (
  call: IncomingCall,
  outboundOnly = false,
): string | null => {
  const active =
    (!outboundOnly || call.origin === "staff_outbound") &&
    call.status === "bridged" &&
    call.settled === null;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const tick = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(tick);
  }, [active]);
  if (!active) return null;
  const startedAtMs =
    call.origin === "staff_outbound" && call.startedAt
      ? new Date(call.startedAt).getTime()
      : call.bridgedAtMs;
  if (startedAtMs === null || !Number.isFinite(startedAtMs)) return null;
  return formatSeconds(Math.max(0, Math.round((now - startedAtMs) / 1000)));
};

export const settledOutcome = (settled: IncomingCall["settled"]) =>
  settled
    ? settled.rowRead
      ? outboundCallOutcome("ended", settled.disposition, settled.outcome)
      : OUTBOUND_ENDED_UNREAD
    : null;

/** What a call carried on this computer's headset shows and does; shared by this panel and the call bubble. */
export const useHeadsetCall = (call: IncomingCall) => {
  const context = useCallContext(call);
  const close = useIncomingCallStore((s) => s.close);
  const softCall = useSoftphoneStore((s) => s.call);
  const ring = useSoftphoneStore((s) => s.incomingRing);
  const dropped = useSoftphoneStore((s) => s.droppedCallUuid === call.callUuid);
  // The active device, not the preference: they diverge on a failed switch.
  const speaker = useSoftphoneStore(
    (s) => s.speakers.find((d) => d.id === s.activeSpeakerId)?.label ?? null,
  );
  const openCalls = useOpenCalls();
  const talkTime = useTalkTime(call);
  const offered = useOutboundLines();

  const inbound = call.origin === "inbound";
  const number = inbound
    ? call.fromNumber
      ? formatPhoneNumber(call.fromNumber)
      : null
    : call.recipientE164
      ? formatPhoneNumber(call.recipientE164)
      : null;
  const name = inbound
    ? (contextCallerName(call, context.data) ?? number ?? "Incoming caller")
    : (call.clientName ?? call.callerName ?? number ?? "Placing a call");
  const pets = call.clientPatients?.length
    ? call.clientPatients.join(", ")
    : null;

  const settled = call.settled;
  const outcome = settledOutcome(settled);
  const bridged = call.status === "bridged";
  const live = softCall !== null && !settled;
  return {
    call,
    context,
    inbound,
    name,
    number,
    pets,
    outcome,
    bridged,
    disclosure: disclosureNoteFor(bridged, call.disclosureState),
    phase: PHASE[call.status] ?? { word: "Calling", aside: "" },
    muted: softCall?.muted ?? false,
    ending: softCall?.state === "ending",
    live,
    dropped,
    speaker,
    talkTime,
    remindDisclosure:
      !inbound &&
      !outcome &&
      !dropped &&
      offered.status === "ready" &&
      !offered.outboundDisclosureSpoken,
    // A ring with no call identity for `markPlacedCall` to match: offered, never auto-answered.
    unattributedRing:
      !inbound && !live && !outcome && !dropped && ring?.callUuid === null,
    openCalls,
    dismiss: () => close(call.callUuid),
    view: () => {
      close(call.callUuid);
      openCalls();
    },
  };
};

type HeadsetCall = ReturnType<typeof useHeadsetCall>;

export const HeadsetStatus = ({ headset }: { headset: HeadsetCall }) => {
  const { call, outcome, dropped, live, bridged, disclosure, muted, phase } =
    headset;
  return (
    <div className="flex min-w-0 flex-1 items-center justify-between gap-2">
      {outcome ? (
        <OutcomeChip label={outcome.label} tone={outcome.tone} />
      ) : dropped && !live ? (
        <Swipe tone="stopped" className="text-annotation-caps">
          Call dropped
        </Swipe>
      ) : (
        <Swipe
          tone="working"
          className="text-annotation-caps"
          icon={<span className={PULSE_DOT} />}
        >
          {bridged
            ? disclosure.playing
              ? "Playing recording notice…"
              : "On the call"
            : phase.word}
        </Swipe>
      )}
      {!outcome && !dropped && bridged && !disclosure.playing && muted && (
        <span
          className="rounded-sm bg-yellow-wash px-2 py-0.5 text-annotation-caps text-ink"
          data-testid="softphone-muted"
        >
          Muted
        </span>
      )}
      {!outcome && !dropped && !bridged && phase.aside && (
        <span
          className="text-fine-print text-annotation"
          data-testid="softphone-phase"
        >
          {phase.aside}
        </span>
      )}
      {call.unreachable && !outcome && (
        <span className="text-fine-print text-annotation">
          Can't reach Brian to update this call.
        </span>
      )}
    </div>
  );
};

export const HeadsetNotes = ({ headset }: { headset: HeadsetCall }) => (
  <>
    {!headset.outcome && !headset.dropped && headset.disclosure.note && (
      <p
        className="mt-1 text-fine-print text-annotation"
        data-testid="softphone-disclosure-note"
      >
        {headset.disclosure.note}
      </p>
    )}
    {headset.remindDisclosure && (
      <StickyNote
        className="mt-2.5 min-h-0 p-2.5 pt-3"
        data-testid="softphone-disclosure-reminder"
      >
        <p className="text-sm text-ink-body">
          Brian isn't playing the recording notice. Tell them this call is
          recorded.
        </p>
      </StickyNote>
    )}
    {headset.dropped && !headset.live && !headset.outcome && (
      <p
        className="mt-2.5 text-fine-print text-ink-stopped"
        data-testid="softphone-dropped"
      >
        This computer lost the call. Brian is ending it.
      </p>
    )}
  </>
);

export const HeadsetButtons = ({
  headset,
  size,
}: {
  headset: HeadsetCall;
  size: "sm" | "md";
}) => (
  <>
    <Button
      variant="outline"
      size={size}
      aria-pressed={headset.muted}
      disabled={!headset.bridged || headset.ending}
      onClick={toggleSoftphoneMute}
      data-testid="softphone-mute"
    >
      {headset.muted ? "Unmute" : "Mute"}
    </Button>
    <Button
      variant="outline"
      size={size}
      busy={headset.ending}
      onClick={hangupSoftphoneCall}
      className="border-ink-stopped text-ink-stopped"
      data-testid="softphone-hangup"
    >
      Hang up
    </Button>
  </>
);

export const SpeakerLine = ({
  headset,
  className,
}: {
  headset: HeadsetCall;
  className?: string;
}) => (
  <div
    className={cn(
      "flex items-center justify-between gap-2 text-fine-print text-annotation",
      className,
    )}
  >
    <span className="truncate">{headset.speaker ?? "Default speaker"}</span>
    <button
      type="button"
      className="cursor-pointer underline decoration-dotted"
      onClick={headset.openCalls}
      data-testid="softphone-devices"
    >
      change
    </button>
  </div>
);

/** The corner for a call carried on this computer's headset. Identity is RUM-hidden (README). */
export const SoftphonePanel = ({ call }: { call: IncomingCall }) => {
  const headset = useHeadsetCall(call);
  const { dragging, moved, dragProps } = useDragOffset();
  const {
    context,
    inbound,
    name,
    number,
    pets,
    outcome,
    dropped,
    live,
    talkTime,
    unattributedRing,
  } = headset;

  return (
    <PaperPanel
      variant="card"
      lifted
      role="status"
      data-testid="softphone-panel"
      {...dragProps}
      className={cn(
        "relative flex max-h-[calc(100dvh-var(--call-popup-space,7rem))] max-w-[calc(100vw-2rem)] flex-col border-t-[3px] p-3.5 pb-3",
        inbound && context.expanded ? "w-[30rem]" : "w-80",
        dragging ? "cursor-grabbing select-none" : "cursor-grab",
        dragging || moved ? "z-20" : "z-10",
        "[animation:bds-slip-in_220ms_var(--ease-paper)_backwards] motion-reduce:animate-none",
        outcome
          ? "border-t-rule"
          : dropped
            ? "border-t-ink-stopped"
            : "border-t-accent",
      )}
    >
      <div className="shrink-0">
        <HeadsetStatus headset={headset} />
        <HeadsetNotes headset={headset} />
        <div className="mt-3" data-dd-privacy="hidden">
          <p className="truncate font-display text-xl font-semibold text-ink">
            {name}
          </p>
          {!inbound && pets && (
            <p className="mt-0.5 truncate text-sm text-ink-body">{pets}</p>
          )}
          {number && name !== number && (
            <p className="mt-1 font-mono text-fine-print text-annotation">
              {number}
            </p>
          )}
        </div>
      </div>
      {inbound && (
        <div
          data-call-context-scroll
          className="min-h-0 overflow-y-auto overscroll-contain"
        >
          <CallContextPanel context={context} />
        </div>
      )}
      <div className="shrink-0">
        {talkTime && (
          <p
            className="mt-3.5 font-mono text-3xl font-medium tabular-nums text-ink"
            data-testid="softphone-talk-time"
          >
            {talkTime}
            <span className="ml-2 align-top text-annotation-caps text-annotation">
              talk time
            </span>
          </p>
        )}

        {outcome ? (
          <div className="mt-3 flex items-center justify-between gap-2">
            <span className="text-fine-print text-annotation">
              {outcome.helper ?? "Summary arrives in the call log in a minute."}
            </span>
            <Button
              variant="accent"
              size="sm"
              data-testid="softphone-view"
              onClick={headset.view}
            >
              View call
            </Button>
          </div>
        ) : live ? (
          <>
            <div className="mt-3.5 grid grid-cols-2 gap-2">
              <HeadsetButtons headset={headset} size="md" />
            </div>
            <SpeakerLine
              headset={headset}
              className="mt-2.5 border-t border-dotted border-rule pt-2.5"
            />
          </>
        ) : (
          <div className="mt-3 flex items-center justify-end gap-1">
            {unattributedRing && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={declineIncoming}
                  data-testid="softphone-decline"
                >
                  Decline
                </Button>
                <Button
                  variant="accent"
                  size="sm"
                  onClick={() => answerIncoming(call.callUuid)}
                  data-testid="softphone-answer"
                >
                  Answer
                </Button>
              </>
            )}
            <Button variant="ghost" size="sm" onClick={headset.dismiss}>
              Dismiss
            </Button>
          </div>
        )}

        <HoldStrip />
      </div>
    </PaperPanel>
  );
};

const isHeld = (c: IncomingCall) => c.holdState === "held";

const heldAtMs = (c: IncomingCall): number | null => {
  if (c.heldAt === null) return null;
  const ms = new Date(c.heldAt).getTime();
  return Number.isFinite(ms) ? ms : null;
};

/** By `held_at`: store order is ring order, not park order. */
const longestHeld = (calls: IncomingCall[]): IncomingCall | null =>
  calls.filter(isHeld).reduce<IncomingCall | null>((best, c) => {
    if (best === null) return c;
    const bestMs = heldAtMs(best);
    const ms = heldAtMs(c);
    if (ms === null) return best;
    if (bestMs === null) return c;
    return ms < bestMs ? c : best;
  }, null);

/** The held slip sits in the pile behind this panel, so the strip surfaces it. */
export const HoldStrip = ({ className }: { className?: string }) => {
  // Reference-stable selections: a filtered array would re-render on every store write.
  const count = useIncomingCallStore((s) => s.calls.filter(isHeld).length);
  const first = useIncomingCallStore((s) => longestHeld(s.calls));
  const heldFor = useHeldFor(first?.heldAt ?? null);
  if (count === 0 || first === null) return null;
  const name = inboundCallerName(first);
  return (
    <div
      className={cn(
        "-mx-3.5 -mb-3 mt-3 flex items-center justify-between gap-2 rounded-b-paper border-t border-rule bg-recess px-3.5 py-2 text-fine-print text-annotation",
        className,
      )}
      data-testid="softphone-hold-strip"
    >
      <span className="inline-flex min-w-0 items-center gap-1.5">
        <FontAwesomeIcon
          icon={faPause}
          aria-hidden="true"
          className="shrink-0 text-[10px] text-ink-flag"
        />
        <span className="truncate">
          <span className="font-semibold text-ink">
            {count === 1 ? "1 caller on hold" : `${count} callers on hold`}
          </span>
          {name && (
            <>
              {" · "}
              <span data-dd-privacy="hidden">{name}</span>
            </>
          )}
        </span>
      </span>
      {heldFor && (
        <span
          className="shrink-0 font-mono tabular-nums"
          data-testid="softphone-hold-strip-time"
        >
          {heldFor}
        </span>
      )}
    </div>
  );
};
